import { useEffect, useState } from "react";
import { Download, RefreshCw, Share, WifiOff, X } from "lucide-react";
import { useI18n } from "../../i18n";
import { applyUpdate, isIosSafari, promptInstall, usePwa } from "../../lib/pwa";

const HINT_KEY = "am_ios_hint_dismissed";

// Counts visits (once per browser session) so the install hint waits for a returning visitor.
const visits = (() => {
  const n = Number(localStorage.getItem("am_visits") || 0) + (sessionStorage.getItem("am_visit") ? 0 : 1);
  localStorage.setItem("am_visits", String(n));
  sessionStorage.setItem("am_visit", "1");
  return n;
})();

const useConsent = () => {
  const [c, setC] = useState(() => !!localStorage.getItem("am_cookie_consent"));
  useEffect(() => {
    const on = () => setC(true), off = () => setC(false);
    window.addEventListener("am:consent", on);
    window.addEventListener("am:cookie-reopen", off);
    return () => { window.removeEventListener("am:consent", on); window.removeEventListener("am:cookie-reopen", off); };
  }, []);
  return c;
};

export const InstallButton = ({ className = "am-btn am-btn-ghost", id = "install-app-button" }) => {
  const { t } = useI18n();
  const { canInstall } = usePwa();
  if (!canInstall) return null;
  return <button type="button" className={className} onClick={promptInstall} data-testid={id}><Download className="h-4 w-4" /><span>{t("pwa.install")}</span></button>;
};

export const IosInstallHint = () => {
  const { t } = useI18n();
  const { standalone } = usePwa();
  const [hidden, setHidden] = useState(() => localStorage.getItem(HINT_KEY) === "1");
  const consent = useConsent();
  if (hidden || standalone || !isIosSafari() || !consent || visits < 2) return null;
  const close = () => { localStorage.setItem(HINT_KEY, "1"); setHidden(true); };
  return (
    <div className="am-pwa-bar" role="dialog" aria-label={t("pwa.install")} data-testid="ios-install-hint">
      <Share className="h-5 w-5 shrink-0" />
      <p className="m-0 flex-1 text-sm">{t("pwa.iosHint")}</p>
      <button type="button" className="am-pwa-x" onClick={close} aria-label={t("pwa.dismiss")} data-testid="ios-install-hint-dismiss"><X className="h-5 w-5" /></button>
    </div>
  );
};

export const UpdateBanner = () => {
  const { t } = useI18n();
  const { updateReady } = usePwa();
  const [closed, setClosed] = useState(false);
  if (!updateReady || closed) return null;
  return (
    <div className="am-pwa-bar is-top" role="status" data-testid="update-banner">
      <RefreshCw className="h-5 w-5 shrink-0" />
      <p className="m-0 flex-1 text-sm">{t("pwa.newVersion")}</p>
      <button type="button" className="am-btn am-btn-primary am-btn-sm" onClick={applyUpdate} data-testid="update-reload-button"><span>{t("pwa.reload")}</span></button>
      <button type="button" className="am-pwa-x" onClick={() => setClosed(true)} aria-label={t("pwa.dismiss")} data-testid="update-banner-dismiss"><X className="h-5 w-5" /></button>
    </div>
  );
};

export const OfflineScreen = () => {
  const { t } = useI18n();
  const { online } = usePwa();
  if (online) return null;
  return (
    <div className="am-offline" role="alert" data-testid="offline-screen">
      <WifiOff className="h-10 w-10" />
      <h1 className="am-h1 m-0 text-2xl">{t("pwa.offlineTitle")}</h1>
      <p className="am-hint m-0 max-w-sm text-center">{t("pwa.offlineBody")}</p>
      <button type="button" className="am-btn am-btn-primary" onClick={() => window.location.reload()} data-testid="offline-retry-button"><span>{t("pwa.retry")}</span></button>
    </div>
  );
};
